import { Form, Input, InputNumber, Modal, Select } from "antd";
import React, { useEffect, useState } from "react";
import {
  selectOptionsComparator,
  selectOptionsHtmlComparator,
  SelectOptionsHtmlType,
  SelectOptionsType,
} from "../../../utils/select-helpers";
import { getPoolsId } from "../../../api/pool/getPoolsId";
import { putPoolsId } from "../../../api/pool/putPoolsId";
import { getLocationsUnPooled } from "../../../api/pool/getLocationsUnpooled";
import { IoFileTray } from "react-icons/io5";

interface Props {
  isVisible: boolean;
  handleClose: (refreshData?: boolean) => void;
  editPoolRecordId?: number;
}

interface State {
  isModalVisible: boolean;
  assignedLocations?: SelectOptionsType[];
  assignedSipNumbers?: SelectOptionsType[];

  unpooledLocations?: SelectOptionsType[];

  assignedSipNumbersFiltered?: SelectOptionsType[];
  locationsOptions?: SelectOptionsHtmlType[];

  sipNumbersQuery: number | null;

  submitError?: string;
}

const initialState: State = {
  isModalVisible: false,
  assignedLocations: undefined,
  assignedSipNumbers: undefined,
  assignedSipNumbersFiltered: undefined,
  locationsOptions: undefined,
  sipNumbersQuery: null,
  submitError: "",
};

const EditPoolFormModal: React.FC<Props> = ({
  isVisible,
  handleClose,
  editPoolRecordId,
}) => {
  const [form] = Form.useForm();

  const [state, setState] = useState<State>(initialState);

  const {
    isModalVisible,
    assignedLocations,
    assignedSipNumbers,
    unpooledLocations,
    locationsOptions,
    assignedSipNumbersFiltered,
    sipNumbersQuery,
    submitError,
  } = state;

  const handleCancel = () => {
    form.resetFields();
    setState((prevState) => ({
      ...prevState,
      submitError: "",
    }));
    handleClose();
  };

  const handleSubmit = () => {
    setState((prevState) => ({
      ...prevState,
      submitError: "",
    }));
    form
      .validateFields()
      .then((formFields) => {
        console.log("onOk formFields: %o", formFields);
        const payload = {
          id: editPoolRecordId || -1,
          name: formFields.poolName || "-1",
          locationNames: formFields.locations.map(
            (location: SelectOptionsHtmlType) => location.value
          ),
          sipNumberIds:
            assignedSipNumbers?.map((sipNumber: SelectOptionsHtmlType) =>
              Number(sipNumber.value)
            ) || [],
          isActive: true,
          threshold: formFields.threshold,
        };
        console.log("postPools() payload: %o", payload);
        putPoolsId(payload).then((postResponse) => {
          console.log("submitResponse:%o", postResponse);
          if (postResponse?.error) {
            setState((prevState) => ({
              ...prevState,
              submitError: postResponse.error,
            }));
          } else {
            setState((prevState) => ({
              ...prevState,
              submitError: "",
            }));
            form.resetFields();
            handleClose(true);
          }
        });
      })
      .catch((err) => {
        console.log("onOk err: %o", err);
      });
  };

  /**
   * In the dropdown for Locations, we wish to present
   *
   * [1] Locations already assigned to the pool, (assignedLocations)
   * [2] plus unpooled locations (unpooledLocations)
   *
   * We present [1] in bold face, so that the user may readily identify current assignments
   * and distinguish them from newly added ones.
   *
   */

  useEffect(() => {
    if (!form || !isVisible) {
      return;
    }

    const assignedSipNumbersMarkedUp: SelectOptionsHtmlType[] =
      assignedSipNumbers
        ?.map((assignedSipNumber) => {
          return {
            title: "",
            label: <>{assignedSipNumber.label}</>,
            value: assignedSipNumber.value,
          };
        })
        .sort(selectOptionsHtmlComparator) || [];

    form.setFieldsValue({
      sipNumbers: assignedSipNumbersMarkedUp,
    });
  }, [assignedSipNumbers, form, isVisible]);

  useEffect(() => {
    if (!form || !isVisible) {
      return;
    }

    const unpooledLocationsMarkedUp =
      unpooledLocations?.map((unpooledLocation) => {
        return {
          title: "",
          label: <>{unpooledLocation.label}</>,
          value: unpooledLocation.value,
        };
      }) || [];

    const assignedLocationsMarkedUp =
      assignedLocations?.map((assignedLocation) => {
        return {
          title: "",
          label: <b>{assignedLocation.label}</b>,
          value: assignedLocation.value,
        };
      }) || [];

    const locationsOptions = [
      ...unpooledLocationsMarkedUp,
      ...assignedLocationsMarkedUp,
    ].sort(selectOptionsHtmlComparator);

    console.log("locationsOptionsMarkedUp: %o", locationsOptions);

    setState((prevState) => ({
      ...prevState,
      locationsOptions,
    }));

    form.setFieldsValue({
      locations: assignedLocationsMarkedUp,
    });
  }, [assignedLocations, unpooledLocations, form, isVisible]);

  useEffect(() => {
    if (isVisible !== isModalVisible) {
      form.resetFields();
      setState((prevState) => ({
        ...prevState,
        ...initialState,
        isModalVisible: isVisible,
      }));

      if (!isVisible) return;

      console.log(
        "<EditPoolFormModal/> editPoolRecordId: %o",
        editPoolRecordId
      );

      getPoolsId({ poolId: `${editPoolRecordId}` }).then((response) => {
        if (response.data) {
          const locationsOptions = (response.data.locationNames || [])
            .map((location) => ({
              title: "",
              value: `${location}`,
              label: `${location}`,
            }))
            .sort(selectOptionsComparator);
          const sipNumbersOptions = (response.data.sipNumbers || []).map(
            (sipNumber) => ({
              title: "",
              value: `${sipNumber.id}`,
              label: `${sipNumber.phoneNumber}`,
            })
          );
          console.log("getPoolsId(): %o", response.data);
          console.log("locationsOptions: %o", locationsOptions);
          setState((prevState) => ({
            ...prevState,
            assignedLocations: locationsOptions, //triggers useEffect[assignedLocations, unpooledLocations, form]
            assignedSipNumbers: sipNumbersOptions, //triggers useEffect[assignedSipNumbers, form]
            assignedSipNumbersFiltered: sipNumbersOptions,
          }));
          form.setFieldsValue({
            poolName: response.data.name,
            threshold: response.data.threshold,
          });
        }
      });

      getLocationsUnPooled().then((response) => {
        if (response.data) {
          console.log("getLocationsUnPooled(): %o", response.data);
          setState((prevState) => ({
            ...prevState,
            unpooledLocations: response.data, //triggers useEffect[assignedLocations, unpooledLocations, form]
          }));
        }
      });
    }

    // eslint-disable-next-line
  }, [isVisible, isModalVisible]);

  const filterNumbers = (query: number | null) => {
    setState((prev) => ({ ...prev, sipNumbersQuery: query }));
    if (!query) {
      setState((prev) => ({
        ...prev,
        assignedSipNumbersFiltered: assignedSipNumbers,
      }));
      return;
    }
    const assignedSipNumbersFiltered = assignedSipNumbers?.filter((item) =>
      String(item.label).includes(String(query))
    );
    setState((prev) => ({
      ...prev,
      assignedSipNumbersFiltered: assignedSipNumbersFiltered,
    }));
  };

  return (
    <Modal
      title="Edit Pool"
      maskClosable={false}
      open={isModalVisible}
      onCancel={handleCancel}
      cancelText="Cancel"
      okText="Submit"
      onOk={handleSubmit}
      okButtonProps={{
        className: "bdo-red-button-modal",
      }}
      cancelButtonProps={{
        className: "bdo-grey-button-modal",
      }}
    >
      <div className="my-4"></div>

      <Form form={form} labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
        <Form.Item
          label="Pool Name:"
          name="poolName"
          rules={[
            { required: true, message: "Enter the pool name" },
            { max: 100, message: "Pool name cannot exceed 100 characters." },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="locations"
          label="Locations:"
          rules={[{ required: true, message: "This field is required" }]}
        >
          <Select
            placeholder="Select locations"
            mode="multiple"
            onChange={(values: string[], selectedOptions = []) => {
              if (Array.isArray(selectedOptions)) {
                const sortedValues =
                  selectedOptions?.sort(selectOptionsHtmlComparator) || [];
                form.setFieldsValue({ locations: sortedValues });
              }
            }}
            allowClear
            options={locationsOptions}
          ></Select>
        </Form.Item>

        <div className="flex flex-col gap-3 mb-4">
          <span>Numbers</span>
          <InputNumber
            className="w-full"
            onChange={filterNumbers}
            min={0}
            max={99999999999}
            value={sipNumbersQuery}
            placeholder={
              assignedSipNumbers && assignedSipNumbers?.length > 0
                ? "Search Number"
                : "No numbers assigned"
            }
            disabled={
              assignedSipNumbers && assignedSipNumbers?.length > 0
                ? false
                : true
            }
          />
          {assignedSipNumbers && assignedSipNumbers?.length > 0 && (
            <ul
              className="grid grid-cols-5 text-center bg-[#f1f1f1] py-1 max-h-[300px] overflow-y-scroll"
              style={{ scrollbarWidth: "thin" }}
            >
              {assignedSipNumbersFiltered &&
              assignedSipNumbersFiltered?.length > 0 ? (
                assignedSipNumbersFiltered?.map((item) => {
                  return <li key={item.value}>{item.label}</li>;
                })
              ) : (
                <li className="flex flex-col items-center justify-center w-full col-start-2 col-end-5 h-[290px]">
                  <IoFileTray color="#b1b9b6" size={30} />
                  <span className="text-[#9d9e9e]">No matching results</span>
                </li>
              )}
            </ul>
          )}
        </div>

        <Form.Item
          name="threshold"
          label="Threshold Value:"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          rules={[
            { required: true, message: "This field is required" },
            {
              type: "number",
              min: 1,
              max: 50,
              message: "Threshold value must be a number between 1 and 50",
            },
          ]}
        >
          <InputNumber
            className="h-8 w-full"
            placeholder="Enter a number between 0 and 50"
          />
        </Form.Item>
      </Form>

      {submitError && <div className="text-red-500">{submitError}</div>}
    </Modal>
  );
};

export default EditPoolFormModal;
